import React, { useState, useEffect, useRef } from "react";
import { API_BASE_URL, enhancedFetch } from "../services/api";
import { format } from "date-fns";

const NodeManagement = ({
  token,
  setModeDiv,
  setSuccess,
  setError,
  isAdmin,
  onUnauthorized,
}) => {
  const [nodes, setNodes] = useState([]);
  const [users, setUsers] = useState([]); // 用户列表
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const currentPageRef = useRef(currentPage);
  const [totalPages, setTotalPages] = useState(0);
  const [totalNodes, setTotalNodes] = useState(0);
  const [statusFilter, setStatusFilter] = useState("all"); // 运行状态过滤
  const statusFilterRef = useRef(statusFilter);
  const [stakeFilter, setStakeFilter] = useState("all"); // 质押状态过滤
  const stakeFilterRef = useRef(stakeFilter);
  const [pubkeyFilter, setPubkeyFilter] = useState(""); // 公钥过滤
  const pubkeyFilterRef = useRef(pubkeyFilter);
  const [assignmentFilter, setAssignmentFilter] = useState("all"); // 分配状态过滤
  const assignmentFilterRef = useRef(assignmentFilter);
  const manualRefreshRef = useRef(false);
  const expiringFilterRef = useRef(false);
  const EXPIRING_DAYS_WINDOW = 30;
  const [selectedNodes, setSelectedNodes] = useState(new Set()); // 选中的节点
  const [showBatchDropdown, setShowBatchDropdown] = useState(false); // 批量操作下拉框显示状态
  const [batchOperationLoading, setBatchOperationLoading] = useState(false); // 批量操作等待状态

  // 新增状态：新增节点弹窗相关
  const [showAddNodeModal, setShowAddNodeModal] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState("");
  // 新增节点相关字段
  const [nodePubkey, setNodePubkey] = useState("");
  const [tenantName, setTenantName] = useState("");
  const [rentalTime, setRentalTime] = useState("");
  const [walletAddress, setWalletAddress] = useState("");

  // 修改节点信息
  const [modifyNodeModal, setModifyNodeModal] = useState(false);

  const handleModify = (pubkey) => {
    const currentNode = nodes.find((node) => node.pubkey === pubkey);
    setTenantName(currentNode.tenant_name);
    let rtime = null;
    if (currentNode.rental_time != undefined && currentNode.rental_time != 0) {
      rtime = format(currentNode.rental_time, "yyyy-MM-dd HH:mm:ss");
    }
    setRentalTime(rtime);
    setWalletAddress(currentNode.wallet_address);
    setSelectedRegion(currentNode.node_info);
    setNodePubkey(currentNode.pubkey);
    let aun = currentNode.assigned_username;
    if (aun == undefined) {
      aun = "";
    }
    setUserSearch(aun);
    setSelectedUser(currentNode.assigned_id);
    setModifyNodeModal(true);
    setIsUserDropdownOpen(false);
  };

  const refreshNodes = (opts = {}) => {
    const {
      page = currentPage,
      runningStatus = statusFilter,
      stakeStatus = stakeFilter,
      pubkey = pubkeyFilter,
      assignment = assignmentFilter,
      expiringFilter = expiringFilterRef.current,
    } = opts;
    fetchNodes(page, runningStatus, stakeStatus, pubkey, assignment, expiringFilter);
  };

  const modifyConfirm = async () => {
    try {
      setModifyNodeModal(false);
      setLoading(true);
      setError("");
      const date = new Date(rentalTime);
      const timestamp = date.getTime();
      const response = await fetch(`${API_BASE_URL}/scripts/modifynode`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          pubkey: nodePubkey,
          region: selectedRegion,
          user: userSearch || undefined,
          id: selectedUser || undefined,
          tenant_name: tenantName || undefined,
          rental_time: timestamp || undefined,
          wallet_address: walletAddress || undefined,
        }),
      });

      if (!response.ok) {
        throw new Error("修改节点失败");
      }

      const data = await response.json();
      setSuccess(
        <>
          公钥:{" "}
          <span
            className="info-value pubkey-full"
            title={data.result.pubkey}
            onClick={(e) => {
              if (!batchOperationLoading) {
                copyToClipboard(data.result.pubkey, e);
              }
            }}
            style={{
              cursor: batchOperationLoading ? "not-allowed" : "pointer",
              position: "relative",
              opacity: batchOperationLoading ? 0.7 : 1,
            }}
          >
            {data.result.pubkey}
            <span className="copy-icon" style={{ marginLeft: "5px" }}>
              📋
            </span>
          </span>
        </>
      );
      refreshNodes();
      setShowAddNodeModal(false);
      setSelectedRegion("");
      setNodePubkey("");
      setUserSearch("");
      setSelectedUser("");
      setTenantName("");
      setRentalTime("");
      setWalletAddress("");
    } catch (err) {
      setError("修改节点失败: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // 自动刷新相关状态
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(30); // 默认30秒
  const [showRefreshDropdown, setShowRefreshDropdown] = useState(false); // 刷新下拉框显示状态
  const autoRefreshTimeout = useRef(null);

  // 新增状态用于查看功能
  const [viewContent, setViewContent] = useState(null); // 查看的内容
  const [showViewModal, setShowViewModal] = useState(false); // 是否显示查看弹窗
  const [viewLoading, setViewLoading] = useState(false); // 查看内容加载状态

  // 节点分配相关状态
  const [showAssignModal, setShowAssignModal] = useState(false); // 是否显示分配弹窗
  const [assignLoading, setAssignLoading] = useState(false); // 分配操作加载状态
  const [selectedUser, setSelectedUser] = useState(""); // 选中的用户
  const [assignRemark, setAssignRemark] = useState(""); // 分配备注
  const [expiringFilter, setExpiringFilter] = useState(false);
  useEffect(() => {
    expiringFilterRef.current = expiringFilter;
  }, [expiringFilter]);

  // 用户搜索相关状态
  const [userSearch, setUserSearch] = useState(""); // 用户搜索关键词（用于分配弹窗）
  const [userFilterSearch, setUserFilterSearch] = useState(""); // 用户搜索关键词（用于分配状态过滤器）
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false); // 用户下拉框是否打开
  const [showUserFilterSearch, setShowUserFilterSearch] = useState(false); // 是否显示用户过滤搜索框

  // 用于防抖的超时引用
  const pubkeyFilterTimeout = useRef(null);

  // 获取节点数据
  const fetchNodes = async (
    page = 1,
    runningStatus = statusFilter,
    stakeStatus = stakeFilter,
    pubkey = pubkeyFilter,
    assignment = assignmentFilter,
    expiringFilter = expiringFilterRef.current
  ) => {
    try {
      setLoading(true);
      setError("");

      if (pubkey.toLowerCase().startsWith("0x")) {
        pubkey = pubkey.substring(2);
      }
      const finalExpiringDays =
        expiringFilter !== undefined && expiringFilter != false
          ? EXPIRING_DAYS_WINDOW
          : undefined;
      const response = await enhancedFetch(
        `${API_BASE_URL}/scripts/nodes/?page=${page}&size=6&assignment=${assignment}&running_status=${runningStatus}&stake_status=${stakeStatus}&pubkey=${pubkey || ""}${finalExpiringDays ? `&expiring_days=${finalExpiringDays}` : ""}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
        onUnauthorized
      );

      if (!response.ok) {
        throw new Error("获取节点数据失败");
      }

      const data = await response.json();

      // 根据分配状态过滤节点
      let filteredNodes = data.nodes;
      if (assignment === "assigned") {
        filteredNodes = data.nodes.filter((node) => node.assigned_username);
      } else if (assignment === "unassigned") {
        filteredNodes = data.nodes.filter((node) => !node.assigned_username);
      }

      setNodes(filteredNodes);
      setTotalPages(data.total_pages);
      setTotalNodes(data.total);
      setLoading(false);
      if (manualRefreshRef.current) {
        setSuccess("已刷新当前筛选数据");
        setTimeout(() => setSuccess(""), 2000);
        manualRefreshRef.current = false;
      }

      // 如果启用了自动刷新，设置下次刷新
      if (autoRefreshEnabled) {
        scheduleNextRefresh();
      }
    } catch (err) {
      setError("网络异常或服务器不可用，已保留当前数据");
      console.warn("fetchNodes error:", err);
      setLoading(false);
    }
  };

  // 获取用户列表
  const fetchUsers = async () => {
    try {
      const response = await enhancedFetch(
        `${API_BASE_URL}/scripts/users/`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
        onUnauthorized
      );

      if (!response.ok) {
        throw new Error("获取用户列表失败");
      }

      const data = await response.json();
      setUsers(data);
    } catch (err) {
      setError("获取用户列表失败: " + err.message);
    }
  };

  // 新增节点
  const createNode = async () => {
    // 显示新增节点弹窗而不是直接创建
    setShowAddNodeModal(true);
    setIsUserDropdownOpen(false);
  };

  // 带地域参数的新建节点函数
  const createNodeWithRegion = async () => {
    try {
      setShowAddNodeModal(false);
      setLoading(true);
      setError("");
      const date = new Date(rentalTime);
      const timestamp = date.getTime();
      const response = await fetch(`${API_BASE_URL}/scripts/newnode`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          region: selectedRegion,
          user: userSearch || undefined,
          id: selectedUser || undefined,
          tenant_name: tenantName || undefined,
          rental_time: timestamp || undefined,
          wallet_address: walletAddress || undefined,
        }),
      });

      if (!response.ok) {
        throw new Error("新增节点失败");
      }

      const data = await response.json();
      setSuccess(
        <>
          公钥:{" "}
          <span
            className="info-value pubkey-full"
            title={data.result.pubkey}
            onClick={(e) => {
              if (!batchOperationLoading) {
                copyToClipboard(data.result.pubkey, e);
              }
            }}
            style={{
              cursor: batchOperationLoading ? "not-allowed" : "pointer",
              position: "relative",
              opacity: batchOperationLoading ? 0.7 : 1,
            }}
          >
            {data.result.pubkey}
            <span className="copy-icon" style={{ marginLeft: "5px" }}>
              📋
            </span>
          </span>
        </>
      );
      refreshNodes();
      setShowAddNodeModal(false);
      setSelectedRegion("");
      setNodePubkey("");
      setUserSearch("");
      setSelectedUser("");
      setTenantName("");
      setRentalTime("");
      setWalletAddress("");
    } catch (err) {
      setError("新增节点失败: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      refreshNodes({ page: 1 });
    }
  }, [token]);

  // 新增一个useEffect，监听isAdmin状态变化
  useEffect(() => {
    if (token && isAdmin) {
      fetchUsers();
    }
  }, [isAdmin, token]);

  // 添加一个useEffect来处理点击下拉框外区域关闭下拉框
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showBatchDropdown && !event.target.closest(".filter-group")) {
        setShowBatchDropdown(false);
      }

      // 点击弹窗外部区域关闭弹窗
      if (showViewModal && event.target.classList.contains("modal-overlay")) {
        setShowViewModal(false);
        setViewContent(null);
      }

      // 点击分配弹窗外部区域关闭弹窗
      if (showAssignModal && event.target.classList.contains("modal-overlay")) {
        setShowAssignModal(false);
        setSelectedUser("");
      }

      // 点击新增节点弹窗外部区域关闭弹窗
      if (
        showAddNodeModal &&
        event.target.classList.contains("modal-overlay")
      ) {
        setShowAddNodeModal(false);
        setSelectedRegion("");
      }

      // 点击新增节点弹窗外部区域关闭弹窗
      if (
        modifyNodeModal &&
        event.target.classList.contains("modal-overlay")
      ) {
        setModifyNodeModal(false);
      }

      // 点击刷新下拉框外部区域关闭下拉框
      if (isUserDropdownOpen && !event.target.closest(".userDropDiv")) {
        setIsUserDropdownOpen(false);
      }

      // 点击刷新下拉框外部区域关闭下拉框
      if (showRefreshDropdown && !event.target.closest(".filter-group2")) {
        setShowRefreshDropdown(false);
      }

      // 点击分配状态过滤器外部区域隐藏下拉菜单
      if (
        showUserFilterSearch &&
        !event.target.closest("#assignmentFilter-container")
      ) {
        setShowUserFilterSearch(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [
    showBatchDropdown,
    showViewModal,
    showAssignModal,
    showRefreshDropdown,
    showUserFilterSearch,
    showAddNodeModal,
    modifyNodeModal,
    isUserDropdownOpen,
  ]);

  // 清理函数 - 组件卸载时清除所有定时器
  useEffect(() => {
    return () => {
      // 清除公钥过滤防抖定时器
      if (pubkeyFilterTimeout.current) {
        clearTimeout(pubkeyFilterTimeout.current);
      }

      // 清除自动刷新定时器
      if (autoRefreshTimeout.current) {
        clearTimeout(autoRefreshTimeout.current);
      }
    };
  }, []);

  useEffect(() => {
    currentPageRef.current = currentPage;
    statusFilterRef.current = statusFilter;
    stakeFilterRef.current = stakeFilter;
    pubkeyFilterRef.current = pubkeyFilter;
    assignmentFilterRef.current = assignmentFilter;
  }, [currentPage, statusFilter, stakeFilter, pubkeyFilter, assignmentFilter]);

  const getStatusClass = (status) => {
    switch (status.toLowerCase()) {
      case "active":
        return "status-active";
      case "inactive":
        return "status-inactive";
      case "staked":
        return "status-staked";
      case "partially staked":
        return "status-partial";
      case "not staked":
        return "status-unstaked";
      default:
        return "";
    }
  };

  const formatPublicKey = (pubkey) => {
    if (!pubkey) return "N/A";
    return `${pubkey.substring(0, 8)}...${pubkey.substring(pubkey.length - 6)}`;
  };

  // 复制公钥到剪贴板
  const copyToClipboard = async (pubkey, e) => {
    e.stopPropagation(); // 防止事件冒泡

    try {
      // 检查clipboard API是否可用
      if (navigator.clipboard && window.isSecureContext) {
        // 在安全上下文中使用现代clipboard API
        await navigator.clipboard.writeText(pubkey);
      } else {
        // 降级处理：使用传统的execCommand方法
        const textArea = document.createElement("textarea");
        textArea.value = pubkey;

        // 避免滚动到底部
        textArea.style.top = "0";
        textArea.style.left = "0";
        textArea.style.position = "fixed";
        textArea.style.opacity = "0";

        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
          const successful = document.execCommand("copy");
          document.body.removeChild(textArea);

          if (!successful) {
            throw new Error("复制命令失败");
          }
        } catch (err) {
          document.body.removeChild(textArea);
          throw new Error("无法复制文本");
        }
      }

      // 显示成功消息
      setSuccess("内容已复制到剪贴板");
      setTimeout(() => setSuccess(""), 3000); // 3秒后清除提示
    } catch (err) {
      setError("复制失败: " + err.message);
      setTimeout(() => setError(""), 3000); // 3秒后清除提示
    }
  };

  // 查看公钥内容
  const viewPublicKeyContent = async (pubkey) => {
    if (batchOperationLoading) return;

    try {
      setViewLoading(true);
      setShowViewModal(true);
      setViewContent(null);

      // 查找当前节点信息
      const currentNode = nodes.find((node) => node.pubkey === pubkey);

      // 获取与该公钥相关的文件
      const response = await fetch(
        `${API_BASE_URL}/scripts/files/by-pubkey/${pubkey}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("获取文件内容失败");
      }

      const files = await response.json();

      // 获取最新的文件内容
      if (files.length > 0) {
        const latestFile = files[0]; // 已按时间倒序排列
        const fileResponse = await fetch(
          `${API_BASE_URL}/scripts/download/${latestFile.filename}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!fileResponse.ok) {
          throw new Error("获取文件内容失败");
        }

        const content = await fileResponse.json();
        let fcontent = content;
        if (currentNode.tenant_name !== undefined) {
          fcontent = {
            tenant_name: currentNode.tenant_name,
            rental_time: currentNode.rental_time,
            node_info: currentNode.node_info,
            wallet_address: currentNode.wallet_address,
            keystore: content,
          };
          if (currentNode.uid != undefined) {
            fcontent.ucloud_username = currentNode.ucloud_username;
            fcontent.ucloud_pw = currentNode.ucloud_pw;
            fcontent.ucloud_web = currentNode.ucloud_web;
          }
        }
        setViewContent({
          pubkey: pubkey,
          file: latestFile,
          content: fcontent,
          node: currentNode, // 添加节点信息
        });
      } else {
        setViewContent({
          pubkey: pubkey,
          file: null,
          content: null,
          node: currentNode, // 添加节点信息
        });
      }
    } catch (err) {
      setError("获取内容失败: " + err.message);
      setTimeout(() => setError(""), 3000);
      setShowViewModal(false);
    } finally {
      setViewLoading(false);
    }
  };

  // 更改页面
  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    refreshNodes({ page: pageNumber });
  };

  // 上一页
  const prevPage = () => {
    if (currentPage > 1) {
      paginate(currentPage - 1);
    }
  };

  // 下一页
  const nextPage = () => {
    if (currentPage < totalPages) {
      paginate(currentPage + 1);
    }
  };

  // 重置过滤器
  const resetFilters = () => {
    setStatusFilter("all");
    setStakeFilter("all");
    setPubkeyFilter("");
    setAssignmentFilter("all");
    setExpiringFilter(false);
    setCurrentPage(1);
    refreshNodes({ page: 1, runningStatus: "all", stakeStatus: "all", pubkey: "", assignment: "all" });
  };

  // 处理运行状态过滤变化
  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    setCurrentPage(1);
    refreshNodes({ page: 1, runningStatus: value });
  };

  // 处理质押状态过滤变化
  const handleStakeFilterChange = (value) => {
    setStakeFilter(value);
    setCurrentPage(1);
    refreshNodes({ page: 1, stakeStatus: value });
  };

  // 处理分配状态过滤变化
  const handleAssignmentFilterChange = (value) => {
    setAssignmentFilter(value);
    setCurrentPage(1);
    refreshNodes({ page: 1, assignment: value });
  };

  // 处理公钥过滤变化
  const handlePubkeyFilterChange = (e) => {
    const value = e.target.value;
    setPubkeyFilter(value);
    setCurrentPage(1);
    // 添加防抖，避免频繁请求
  clearTimeout(pubkeyFilterTimeout.current);
  pubkeyFilterTimeout.current = setTimeout(() => {
      refreshNodes({ page: 1, pubkey: value });
    }, 500);
  };

  // 处理节点选择
  const handleNodeSelect = (pubkey) => {
    const newSelectedNodes = new Set(selectedNodes);
    if (newSelectedNodes.has(pubkey)) {
      newSelectedNodes.delete(pubkey);
    } else {
      newSelectedNodes.add(pubkey);
    }
    setSelectedNodes(newSelectedNodes);
  };

  // 全选/取消全选
  const handleSelectAll = () => {
    // 检查当前页是否已经全选
    const isCurrentPageFullySelected =
      nodes.length > 0 && nodes.every((node) => selectedNodes.has(node.pubkey));

    if (isCurrentPageFullySelected) {
      // 如果当前页已经全选，则取消选择当前页的所有节点
      const newSelectedNodes = new Set(selectedNodes);
      nodes.forEach((node) => newSelectedNodes.delete(node.pubkey));
      setSelectedNodes(newSelectedNodes);
    } else {
      // 否则选择当前页的所有节点
      const newSelectedNodes = new Set(selectedNodes);
      nodes.forEach((node) => newSelectedNodes.add(node.pubkey));
      setSelectedNodes(newSelectedNodes);
    }
  };

  // 清除公钥过滤
  const clearPubkeyFilter = () => {
    setPubkeyFilter("");
    setCurrentPage(1);
    refreshNodes({ page: 1, pubkey: "" });
  };

  // 处理启动节点
  const handleStartNode = async (pubkey) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/scripts/nodes/${pubkey}/start`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "启动节点失败");
      }

      const data = await response.json();
      setSuccess(data.message || "节点启动命令已发送");
      setTimeout(() => setSuccess(""), 3000);

      // 重新加载节点数据以更新状态
      refreshNodes();
    } catch (err) {
      setError("启动节点失败: " + err.message);
      setTimeout(() => setError(""), 3000);
    }
  };

  // 处理停止节点
  const handleStopNode = async (pubkey) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/scripts/nodes/${pubkey}/stop`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "停止节点失败");
      }

      const data = await response.json();
      setSuccess(data.message || "节点停止命令已发送");
      setTimeout(() => setSuccess(""), 3000);

      // 重新加载节点数据以更新状态
      refreshNodes();
    } catch (err) {
      setError("停止节点失败: " + err.message);
      setTimeout(() => setError(""), 3000);
    }
  };

  // 批量启动节点
  const handleBatchStartNodes = async () => {
    try {
      const selectedNodesArray = Array.from(selectedNodes);
      if (selectedNodesArray.length === 0) {
        setError("请至少选择一个节点");
        setTimeout(() => setError(""), 3000);
        return;
      }

      // 设置批量操作等待状态
      setBatchOperationLoading(true);
      setSuccess("");
      setError("");

      // 并行处理所有请求
      const promises = selectedNodesArray.map((pubkey) =>
        fetch(`${API_BASE_URL}/scripts/nodes/${pubkey}/start`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        })
      );

      const results = await Promise.allSettled(promises);
      const successCount = results.filter(
        (result) => result.status === "fulfilled" && result.value.ok
      ).length;
      const failCount = results.length - successCount;

      if (successCount > 0) {
        setSuccess(
          `成功启动 ${successCount} 个节点${failCount > 0 ? `，${failCount} 个节点启动失败` : ""
          }`
        );
      } else {
        setError(`启动节点失败: 所有 ${failCount} 个节点都启动失败`);
      }

      setTimeout(() => {
        setSuccess("");
        setError("");
      }, 3000);

      // 清空选择
      setSelectedNodes(new Set());

      // 重新加载节点数据以更新状态
      refreshNodes();
    } catch (err) {
      setError("批量启动节点失败: " + err.message);
      setTimeout(() => setError(""), 3000);
    } finally {
      // 重置批量操作等待状态
      setBatchOperationLoading(false);
      // 关闭下拉框
      setShowBatchDropdown(false);
    }
  };

  // 批量停止节点
  const handleBatchStopNodes = async () => {
    try {
      const selectedNodesArray = Array.from(selectedNodes);
      if (selectedNodesArray.length === 0) {
        setError("请至少选择一个节点");
        setTimeout(() => setError(""), 3000);
        return;
      }

      // 设置批量操作等待状态
      setBatchOperationLoading(true);
      setSuccess("");
      setError("");

      // 并行处理所有请求
      const promises = selectedNodesArray.map((pubkey) =>
        fetch(`${API_BASE_URL}/scripts/nodes/${pubkey}/stop`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        })
      );

      const results = await Promise.allSettled(promises);
      const successCount = results.filter(
        (result) => result.status === "fulfilled" && result.value.ok
      ).length;
      const failCount = results.length - successCount;

      if (successCount > 0) {
        setSuccess(
          `成功停止 ${successCount} 个节点${failCount > 0 ? `，${failCount} 个节点停止失败` : ""
          }`
        );
      } else {
        setError(`停止节点失败: 所有 ${failCount} 个节点都停止失败`);
      }

      setTimeout(() => {
        setSuccess("");
        setError("");
      }, 3000);

      // 清空选择
      setSelectedNodes(new Set());

      // 重新加载节点数据以更新状态
      refreshNodes();
    } catch (err) {
      setError("批量停止节点失败: " + err.message);
      setTimeout(() => setError(""), 3000);
    } finally {
      // 重置批量操作等待状态
      setBatchOperationLoading(false);
      // 关闭下拉框
      setShowBatchDropdown(false);
    }
  };

  // 打开节点分配弹窗
  const openAssignModal = () => {
    if (selectedNodes.size === 0) {
      setError("请至少选择一个节点");
      setTimeout(() => setError(""), 3000);
      return;
    }
    setShowAssignModal(true);
    setUserSearch(""); // 打开弹窗时清空搜索
    setIsUserDropdownOpen(false); // 关闭下拉框
  };

  // 处理节点分配
  const handleAssignNodes = async () => {
    if (!selectedUser) {
      setError("请选择一个用户");
      setTimeout(() => setError(""), 3000);
      return;
    }

    try {
      setAssignLoading(true);
      setError("");
      setSuccess("");

      const response = await fetch(`${API_BASE_URL}/scripts/assign-nodes`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          pubkeys: Array.from(selectedNodes),
          user_id: selectedUser,
          remark: assignRemark, // 添加备注
        }),
      });

      if (!response.ok) {
        throw new Error("分配请求失败");
      }

      const data = await response.json();

      setSuccess(
        `成功分配 ${data["assigned_nodes_count"]} 个节点给用户: ${data["target_user"]["username"]}`
      );
      setTimeout(() => {
        setSuccess("");
      }, 3000);
      setSelectedNodes(new Set());
      setShowAssignModal(false);
      setSelectedUser("");
      setAssignRemark(""); // 清空备注

      // 重新加载节点数据
      refreshNodes();
    } catch (err) {
      setError("节点分配失败: " + err.message);
    } finally {
      setAssignLoading(false);
      setSelectedUser("");
    }
  };

  const handleRemoveAssignNodes = async () => {
    try {
      setAssignLoading(true);
      setError("");
      setSuccess("");
      const response = await fetch(
        `${API_BASE_URL}/scripts/remove-assign-nodes`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(Array.from(selectedNodes)),
        }
      );

      if (!response.ok) {
        throw new Error("分配请求失败");
      }

      const data = await response.json();

      setSuccess(`成功取消分配 ${data["removed_nodes_count"]} 个节点`);
      setTimeout(() => {
        setSuccess("");
      }, 3000);
      setSelectedNodes(new Set());

      // 重新加载节点数据
      refreshNodes();
    } catch (err) {
      setError("节点取消分配失败: " + err.message);
      setTimeout(() => {
        setError("");
      }, 3000);
    } finally {
      setAssignLoading(false);
    }
  };

  // 安排下一次自动刷新
  const scheduleNextRefresh = () => {
    // 清除之前的定时器
    if (autoRefreshTimeout.current) {
      clearInterval(autoRefreshTimeout.current);
    }

    // 设置新的定时器
    autoRefreshTimeout.current = setInterval(() => {
      refreshNodes({
        page: currentPageRef.current,
        runningStatus: statusFilterRef.current,
        stakeStatus: stakeFilterRef.current,
        pubkey: pubkeyFilterRef.current,
        assignment: assignmentFilterRef.current,
      });
    }, refreshInterval * 1000);
  };

  // 切换自动刷新状态
  const toggleAutoRefresh = () => {
    const newEnabledState = !autoRefreshEnabled;
    setAutoRefreshEnabled(newEnabledState);

    if (newEnabledState) {
      // 启用自动刷新
      scheduleNextRefresh();
      setSuccess(`已启用自动刷新，每${refreshInterval}秒刷新一次`);
    } else {
      // 禁用自动刷新
      if (autoRefreshTimeout.current) {
        clearTimeout(autoRefreshTimeout.current);
        autoRefreshTimeout.current = null;
      }
      setSuccess("已禁用自动刷新");
    }

    // 3秒后清除提示消息
    setTimeout(() => setSuccess(""), 3000);
  };

  const handleExpiringFilterChange = (checked) => {
    setExpiringFilter(checked);
    setCurrentPage(1);
    setSuccess(checked ? "已开启近30天到期筛选" : "已关闭近30天到期筛选");
    setTimeout(() => setSuccess(""), 2000);
    refreshNodes({ page: 1, expiringFilter: checked});
    console.info("Expiring filter toggled:", checked);
  };

  if (loading && nodes.length === 0) {
    return (
      <div className="card">
        <div className="card-header">
          <h2>节点管理</h2>
          <p>管理系统中的节点信息</p>
        </div>
        <div className="loading">正在加载节点数据...</div>
      </div>
    );
  }

  return (
    <>
      {/* 新增节点弹窗 */}
      {(showAddNodeModal || modifyNodeModal) && (
        <form
          onSubmit={showAddNodeModal ? createNodeWithRegion : modifyConfirm}
        >
          <div className="modal-overlay">
            <div className="modal-content">
              <div
                className="modal-header"
                style={{
                  padding: "20px",
                  borderBottom: "1px solid #eee",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <h3 style={{ margin: "0" }}>
                  {modifyNodeModal ? "修改节点" : "新增节点"}
                </h3>
                <button
                  onClick={() => {
                    setShowAddNodeModal(false);
                    setModifyNodeModal(false);
                    setSelectedRegion("");
                    setNodePubkey("");
                    setUserSearch("");
                    setSelectedUser("");
                    setTenantName("");
                    setRentalTime("");
                    setWalletAddress("");
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: "24px",
                    cursor: "pointer",
                    padding: "0",
                    width: "30px",
                    height: "30px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  ×
                </button>
              </div>

              <div
                className="modal-body"
                style={{
                  padding: "20px",
                  overflow: "auto",
                  flexGrow: 1,
                }}
              >
                <div style={{ marginBottom: "15px" }}>
                  <label
                    htmlFor="regionSelect"
                    style={{ display: "block", marginBottom: "5px" }}
                  >
                    选择节点地域:
                  </label>
                  <select
                    id="regionSelect"
                    value={selectedRegion}
                    onChange={(e) => setSelectedRegion(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "12px",
                      border: "1px solid #ddd",
                      borderRadius: "6px",
                      fontSize: "15px",
                    }}
                  >
                    <option value="">请选择地域</option>
                    <option value="uc-洛杉矶">uc-洛杉矶</option>
                    <option value="uc-香港">uc-香港</option>
                    <option value="uc-华盛顿">uc-华盛顿</option>
                  </select>
                </div>

                <div style={{ marginBottom: "15px" }}>
                  <label
                    htmlFor="tenantName"
                    style={{ display: "block", marginBottom: "5px" }}
                  >
                    租户名称:
                  </label>
                  <input
                    type="text"
                    id="tenantName"
                    value={tenantName}
                    onChange={(e) => setTenantName(e.target.value)}
                    placeholder="请输入租户名称"
                    required
                    style={{
                      width: "100%",
                      padding: "12px",
                      border: "1px solid #ddd",
                      borderRadius: "6px",
                      fontSize: "15px",
                    }}
                  />
                </div>

                <div style={{ marginBottom: "15px" }}>
                  <label
                    htmlFor="rentalTime"
                    style={{ display: "block", marginBottom: "5px" }}
                  >
                    租用时间:
                  </label>
                  <input
                    type="datetime-local"
                    id="rentalTime"
                    value={rentalTime}
                    onChange={(e) => setRentalTime(e.target.value)}
                    placeholder="请选择租用时间"
                    required
                    style={{
                      width: "100%",
                      padding: "12px",
                      border: "1px solid #ddd",
                      borderRadius: "6px",
                      fontSize: "15px",
                    }}
                  />
                </div>

                <div style={{ marginBottom: "15px" }}>
                  <label
                    htmlFor="walletAddress"
                    style={{ display: "block", marginBottom: "5px" }}
                  >
                    钱包地址:
                  </label>
                  <input
                    type="text"
                    id="walletAddress"
                    value={walletAddress}
                    onChange={(e) => setWalletAddress(e.target.value)}
                    placeholder="请输入钱包地址"
                    style={{
                      width: "100%",
                      padding: "12px",
                      border: "1px solid #ddd",
                      borderRadius: "6px",
                      fontSize: "15px",
                    }}
                    required
                  />
                </div>

                <div style={{ marginBottom: "15px" }}>
                  <label
                    htmlFor="nodeUser"
                    style={{ display: "block", marginBottom: "5px" }}
                  >
                    用户 (可选):
                  </label>
                  <div style={{ position: "relative" }} className="userDropDiv">
                    <div
                      style={{
                        position: "relative",
                        marginBottom: "5px",
                      }}
                    >
                      <input
                        type="text"
                        placeholder="搜索用户..."
                        value={userSearch}
                        onChange={(e) => setUserSearch(e.target.value)}
                        onClick={() => setIsUserDropdownOpen(true)}
                        style={{
                          width: "100%",
                          padding: "12px 12px 12px 36px",
                          border: "1px solid #ddd",
                          borderRadius: "6px",
                          fontSize: "15px",
                          boxShadow: "0 1px 2px rgba(0, 0, 0, 0.1)",
                          transition: "all 0.2s ease",
                        }}
                        onFocus={(e) => {
                          e.target.style.boxShadow =
                            "0 0 0 3px rgba(66, 153, 225, 0.2), 0 1px 2px rgba(0, 0, 0, 0.1)";
                          setIsUserDropdownOpen(true);
                        }}
                        onBlur={(e) => {
                          e.target.style.boxShadow =
                            "0 1px 2px rgba(0, 0, 0, 0.1)";
                          // setIsUserDropdownOpen(false);
                        }}
                      />
                      <span
                        style={{
                          position: "absolute",
                          left: "12px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          color: "#999",
                          fontSize: "16px",
                        }}
                      >
                        🔍
                      </span>
                    </div>
                    {isUserDropdownOpen && (
                      <div className="userDrop">
                        {users.filter(
                          (user) =>
                            user.username
                              .toLowerCase()
                              .includes(userSearch.toLowerCase()) && !user.is_admin ||
                            userSearch === ""
                        ).length > 0 ? (
                          users
                            .filter(
                              (user) =>
                                user.username
                                  .toLowerCase()
                                  .includes(userSearch.toLowerCase()) && !user.is_admin ||
                                userSearch === ""
                            )
                            .map((user) => (
                              <div
                                key={user.id}
                                onClick={() => {
                                  setSelectedUser(user.id);
                                  setUserSearch(user.username);
                                  setIsUserDropdownOpen(false);
                                }}
                                style={{
                                  padding: "12px 15px",
                                  cursor: "pointer",
                                  borderBottom: "1px solid #eee",
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                }}
                                onMouseEnter={(e) =>
                                  (e.target.style.backgroundColor = "#f5f7fa")
                                }
                                onMouseLeave={(e) =>
                                  (e.target.style.backgroundColor = "white")
                                }
                              >
                                <span>{user.username}</span>
                                {user.is_superuser && (
                                  <span
                                    style={{
                                      backgroundColor: "#4f46e5",
                                      color: "white",
                                      borderRadius: "12px",
                                      padding: "2px 8px",
                                      fontSize: "12px",
                                    }}
                                  >
                                    管理员
                                  </span>
                                )}
                              </div>
                            ))
                        ) : (
                          <div
                            style={{
                              padding: "12px 15px",
                              color: "#666",
                              fontStyle: "italic",
                            }}
                          >
                            未找到匹配的用户
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div
                  style={{
                    backgroundColor: "#f8f9fa",
                    padding: "15px",
                    borderRadius: "4px",
                  }}
                >
                  <p>
                    确定要在 {selectedRegion ? selectedRegion : "未选择地域"}{" "}
                    {modifyNodeModal ? "修改" : "创建新"}节点吗？
                  </p>
                </div>
              </div>

              <div
                className="modal-footer"
                style={{
                  padding: "15px 20px",
                  borderTop: "1px solid #eee",
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "10px",
                }}
              >
                <button
                  className="secondary-button"
                  onClick={() => {
                    setShowAddNodeModal(false);
                    setModifyNodeModal(false);
                    setSelectedRegion("");
                    setNodePubkey("");
                    setUserSearch("");
                    setSelectedUser("");
                    setTenantName("");
                    setRentalTime("");
                    setWalletAddress("");
                  }}
                >
                  取消
                </button>
                <button
                  type="submit"
                  className="primary-button"
                  disabled={!selectedRegion}
                >
                  确认
                </button>
              </div>
            </div>
          </div>
        </form>
      )}

      {/* 查看内容弹窗 */}
      {viewContent && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div
              className="modal-header"
              style={{
                padding: "20px",
                borderBottom: "1px solid #eee",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <h3 style={{ margin: 0 }}>公钥内容详情</h3>
              <button
                onClick={() => {
                  setShowViewModal(false);
                  setViewContent(null);
                  setModeDiv(null);
                }}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "24px",
                  cursor: "pointer",
                  padding: "0",
                  width: "30px",
                  height: "30px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                ×
              </button>
            </div>

            <div
              className="modal-body"
              style={{
                padding: "20px",
                overflow: "auto",
                flexGrow: 1,
              }}
            >
              {viewLoading ? (
                <div className="loading">正在加载内容...</div>
              ) : viewContent ? (
                <div>
                  {viewContent.content ? (
                    <div>
                      <span
                        className="copy-icon"
                        style={{
                          position: "absolute",
                          right: "28px",
                          cursor: "pointer",
                        }}
                        onClick={(e) => {
                          if (viewContent.content.tenant_name !== undefined) {
                            const pubkey = (/^0x/i.test(viewContent.pubkey)) ? viewContent.pubkey : '0x' + viewContent.pubkey
                            let ucinfo = ""
                            if (viewContent.content.ucloud_username !== undefined) {
                              ucinfo = 'ucloud用户名：' + viewContent.content.ucloud_username + '\nucloud密码：' + viewContent.content.ucloud_pw + '\nucloud网站：' + viewContent.content.ucloud_web + '\n';
                            }
                            copyToClipboard(
                              "租户名称: " +
                              viewContent.content.tenant_name +
                              "\n" +
                              "租用时间: " +
                              new Date(
                                viewContent.content.rental_time
                              ).toLocaleString() +
                              "\n" +
                              "节点信息: " +
                              viewContent.content.node_info +
                              "\n" +
                              "OR钱包地址：" +
                              viewContent.content.wallet_address +
                              "\n" +
                              "公钥: " +
                              pubkey +
                              "\n" + ucinfo +
                              "keystore:\n" +
                              JSON.stringify(viewContent.content.keystore),
                              e
                            );
                          } else {
                            copyToClipboard(
                              JSON.stringify(viewContent.content),
                              e
                            );
                          }
                        }}
                      >
                        📋
                      </span>
                      <pre
                        style={{
                          background: "#f8f9fa",
                          padding: "15px",
                          borderRadius: "4px",
                          overflow: "auto",
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-all",
                          fontSize: "14px",
                          fontFamily: "monospace",
                        }}
                      >
                        {viewContent.content.tenant_name ? (
                          <>
                            租户名称：{viewContent.content.tenant_name}
                            <br />
                            租用时间:{" "}
                            {new Date(
                              viewContent.content.rental_time
                            ).toLocaleString()}
                            <br />
                            节点信息: {viewContent.content.node_info}
                            <br />
                            OR钱包地址：{viewContent.content.wallet_address}
                            <br />
                            公钥: {(/^0x/i.test(viewContent.pubkey)) ? viewContent.pubkey : '0x' + viewContent.pubkey}
                            <br />
                            {viewContent.content.ucloud_username && ('ucloud用户名：' + viewContent.content.ucloud_username + '\nucloud密码：' + viewContent.content.ucloud_pw + '\nucloud网站：' + viewContent.content.ucloud_web + '\n')}
                            keystore: <br />
                            {JSON.stringify(viewContent.content.keystore)}
                          </>
                        ) : (
                          JSON.stringify(viewContent.content, null, 2)
                        )}
                      </pre>
                    </div>
                  ) : viewContent.file && !viewContent.content ? (
                    <div className="error-message">无法加载文件内容</div>
                  ) : (
                    <div>未找到内容</div>
                  )}
                </div>
              ) : (
                <div>未找到内容</div>
              )}
            </div>

            <div
              className="modal-footer"
              style={{
                padding: "15px 20px",
                borderTop: "1px solid #eee",
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <button
                className="primary-button"
                onClick={() => {
                  setShowViewModal(false);
                  setViewContent(null);
                  setModeDiv(null);
                }}
              >
                关闭
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 节点分配弹窗 */}
      {showAssignModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div
              className="modal-header"
              style={{
                padding: "20px",
                borderBottom: "1px solid #eee",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <h3 style={{ margin: "0" }}>分配节点至管理员</h3>
              <button
                onClick={() => {
                  setShowAssignModal(false);
                  setSelectedUser("");
                  setAssignRemark("");
                  setIsUserDropdownOpen(false);
                }}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "24px",
                  cursor: "pointer",
                  padding: "0",
                  width: "30px",
                  height: "30px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                ×
              </button>
            </div>

            <div
              className="modal-body"
              style={{
                padding: "20px",
                overflow: "auto",
                flexGrow: 1,
              }}
            >
              <div style={{ marginBottom: "15px" }}>
                <strong>已选择节点:</strong> {selectedNodes.size} 个
              </div>

              <div style={{ marginBottom: "15px" }}>
                <label
                  htmlFor="userSelect"
                  style={{ display: "block", marginBottom: "5px" }}
                >
                  选择用户:
                </label>
                <div style={{ position: "relative" }} className="userDropDiv">
                  <div
                    style={{
                      position: "relative",
                      marginBottom: "5px",
                    }}
                  >
                    <input
                      type="text"
                      placeholder="搜索用户..."
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      onClick={() => setIsUserDropdownOpen(true)}
                      style={{
                        width: "100%",
                        padding: "12px 12px 12px 36px",
                        border: "1px solid #ddd",
                        borderRadius: "6px",
                        fontSize: "15px",
                        boxShadow: "0 1px 2px rgba(0, 0, 0, 0.1)",
                        transition: "all 0.2s ease",
                      }}
                      onFocus={(e) => {
                        e.target.style.boxShadow =
                          "0 0 0 3px rgba(66, 153, 225, 0.2), 0 1px 2px rgba(0, 0, 0, 0.1)";
                        setIsUserDropdownOpen(true);
                      }}
                      onBlur={(e) =>
                      (e.target.style.boxShadow =
                        "0 1px 2px rgba(0, 0, 0, 0.1)")
                      }
                    />
                    <span
                      style={{
                        position: "absolute",
                        left: "12px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        color: "#999",
                        fontSize: "16px",
                      }}
                    >
                      🔍
                    </span>
                  </div>
                  {isUserDropdownOpen && (
                    <div className="userDrop">
                      {users.filter(
                        (user) =>
                          user.username
                            .toLowerCase()
                            .includes(userSearch.toLowerCase()) && user.is_admin == true
                      ).length > 0 ? (
                        users
                          .filter(
                            (user) =>
                              user.username
                                .toLowerCase()
                                .includes(userSearch.toLowerCase()) && user.is_admin == true
                          )
                          .map((user) => (
                            <div
                              key={user.id}
                              onClick={() => {
                                setSelectedUser(user.id);
                                setUserSearch(user.username);
                                setIsUserDropdownOpen(false);
                              }}
                              style={{
                                padding: "12px 15px",
                                cursor: "pointer",
                                borderBottom: "1px solid #eee",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                              onMouseEnter={(e) =>
                                (e.target.style.backgroundColor = "#f5f7fa")
                              }
                              onMouseLeave={(e) =>
                                (e.target.style.backgroundColor = "white")
                              }
                            >
                              <span>{user.username}</span>
                              {user.is_superuser && (
                                <span
                                  style={{
                                    backgroundColor: "#4f46e5",
                                    color: "white",
                                    borderRadius: "12px",
                                    padding: "2px 8px",
                                    fontSize: "12px",
                                  }}
                                >
                                  管理员
                                </span>
                              )}
                            </div>
                          ))
                      ) : (
                        <div
                          style={{
                            padding: "12px 15px",
                            color: "#666",
                            fontStyle: "italic",
                          }}
                        >
                          未找到匹配的用户
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div style={{ marginBottom: "15px" }}>
                <label
                  htmlFor="assignRemark"
                  style={{ display: "block", marginBottom: "5px" }}
                >
                  备注 (可选):
                </label>
                <textarea
                  id="assignRemark"
                  value={assignRemark}
                  onChange={(e) => setAssignRemark(e.target.value)}
                  placeholder="请输入分配备注..."
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    fontSize: "16px",
                    minHeight: "80px",
                    resize: "vertical",
                  }}
                  disabled={assignLoading}
                />
              </div>

              <div
                style={{
                  backgroundColor: "#f8f9fa",
                  padding: "15px",
                  borderRadius: "4px",
                  overflow: "auto",
                }}
              >
                <strong>选中的节点公钥:</strong>
                <ul
                  style={{
                    marginTop: "10px",
                    paddingLeft: "20px",
                    overflow: "auto",
                  }}
                >
                  {Array.from(selectedNodes).map((pubkey) => (
                    <li
                      key={pubkey}
                      style={{ wordBreak: "break-all", marginBottom: "5px" }}
                    >
                      {formatPublicKey(pubkey)}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div
              className="modal-footer"
              style={{
                padding: "15px 20px",
                borderTop: "1px solid #eee",
                display: "flex",
                justifyContent: "flex-end",
                gap: "10px",
              }}
            >
              <button
                className="secondary-button"
                onClick={() => {
                  setShowAssignModal(false);
                  setSelectedUser("");
                  setAssignRemark("");
                  setIsUserDropdownOpen(false);
                }}
                disabled={assignLoading}
              >
                取消
              </button>
              <button
                className="primary-button"
                onClick={handleAssignNodes}
                disabled={assignLoading || !selectedUser}
              >
                {assignLoading ? "分配中..." : "确认分配"}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* 添加加载蒙板 */}
      {(loading || batchOperationLoading || assignLoading || viewLoading) && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
            borderRadius: "12px",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "10px",
            }}
          >
            <div
              className="loading-spinner"
              style={{
                width: "40px",
                height: "40px",
                border: "4px solid #f3f3f3",
                borderTop: "4px solid #4361ee",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
              }}
            ></div>
            <div>处理中...</div>
          </div>
        </div>
      )}
      <div className="card">
        <div className="card-header">
          <h2>节点管理</h2>
          <p>管理系统中的节点信息</p>
        </div>
        <div>
          {/* 过滤器控件 */}
          <div className="filters">
            <div className="filter-group">
              <label htmlFor="statusFilter">运行状态:</label>
              <select
                id="statusFilter"
                value={statusFilter}
                onChange={(e) => handleStatusFilterChange(e.target.value)}
                className="filter-select"
                disabled={batchOperationLoading}
              >
                <option value="all">全部</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            <div className="filter-group">
              <label htmlFor="stakeFilter">质押状态:</label>
              <select
                id="stakeFilter"
                value={stakeFilter}
                onChange={(e) => handleStakeFilterChange(e.target.value)}
                className="filter-select"
                disabled={batchOperationLoading}
              >
                <option value="all">全部</option>
                <option value="staked">Staked</option>
                <option value="partially staked">Partially Staked</option>
                <option value="not staked">Not Staked</option>
              </select>
            </div>
            {isAdmin && (
              <div className="filter-group">
                <label htmlFor="assignmentFilter">分配状态:</label>
                <div
                  className="filter-select"
                  id="assignmentFilter-container"
                  style={{
                    position: "relative",
                    height: "43px",
                    alignItems: "center",
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                  onClick={() => setShowUserFilterSearch(!showUserFilterSearch)}
                >
                  {/* 触发按钮 */}
                  <span disabled={batchOperationLoading}>
                    {assignmentFilter === "all" && "全部"}
                    {assignmentFilter === "assigned" && "已分配"}
                    {assignmentFilter === "unassigned" && "未分配"}
                    {assignmentFilter !== "all" &&
                      assignmentFilter !== "assigned" &&
                      assignmentFilter !== "unassigned" &&
                      assignmentFilterRef.current}
                  </span>

                  <span
                    style={{
                      color: "#999",
                      pointerEvents: "none",
                    }}
                  >
                    ▼
                  </span>
                  {/* 自定义下拉菜单 */}
                  {showUserFilterSearch && (
                    <div
                      style={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        width: "200px",
                        backgroundColor: "white",
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                        zIndex: 1000,
                        marginTop: "2px",
                        maxHeight: "300px",
                        overflow: "hidden",
                      }}
                    >
                      {/* 搜索框 */}
                      <div
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #eee",
                        }}
                      >
                        <div style={{ position: "relative" }}>
                          <input
                            type="text"
                            placeholder="搜索用户..."
                            value={userFilterSearch}
                            onChange={(e) =>
                              setUserFilterSearch(e.target.value)
                            }
                            style={{
                              width: "100%",
                              padding: "8px 12px 8px 30px",
                              border: "1px solid #ddd",
                              borderRadius: "4px",
                              fontSize: "14px",
                              boxSizing: "border-box",
                            }}
                            onKeyDown={(e) => {
                              // 阻止上下箭头键的默认行为，避免影响选择
                              if (
                                e.key === "ArrowUp" ||
                                e.key === "ArrowDown"
                              ) {
                                e.preventDefault();
                              }

                              // 当按下ESC键时隐藏搜索框
                              if (e.key === "Escape") {
                                setShowUserFilterSearch(false);
                                setUserFilterSearch("");
                              }
                            }}
                            autoFocus
                          />
                          <span
                            style={{
                              position: "absolute",
                              left: "10px",
                              top: "50%",
                              transform: "translateY(-50%)",
                              color: "#999",
                              fontSize: "14px",
                            }}
                          >
                            🔍
                          </span>
                        </div>
                      </div>

                      {/* 选项列表 */}
                      <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                        <div
                          onClick={() => {
                            handleAssignmentFilterChange("all");
                            setShowUserFilterSearch(false);
                          }}
                          style={{
                            padding: "10px 15px",
                            cursor: "pointer",
                            borderBottom: "1px solid #eee",
                          }}
                          onMouseEnter={(e) =>
                            (e.target.style.backgroundColor = "#f5f5f5")
                          }
                          onMouseLeave={(e) =>
                            (e.target.style.backgroundColor = "white")
                          }
                        >
                          全部
                        </div>
                        <div
                          onClick={() => {
                            handleAssignmentFilterChange("assigned");
                            setShowUserFilterSearch(false);
                          }}
                          style={{
                            padding: "10px 15px",
                            cursor: "pointer",
                            borderBottom: "1px solid #eee",
                          }}
                          onMouseEnter={(e) =>
                            (e.target.style.backgroundColor = "#f5f5f5")
                          }
                          onMouseLeave={(e) =>
                            (e.target.style.backgroundColor = "white")
                          }
                        >
                          已分配
                        </div>
                        <div
                          onClick={() => {
                            handleAssignmentFilterChange("unassigned");
                            setShowUserFilterSearch(false);
                          }}
                          style={{
                            padding: "10px 15px",
                            cursor: "pointer",
                            borderBottom: "1px solid #eee",
                          }}
                          onMouseEnter={(e) =>
                            (e.target.style.backgroundColor = "#f5f5f5")
                          }
                          onMouseLeave={(e) =>
                            (e.target.style.backgroundColor = "white")
                          }
                        >
                          未分配
                        </div>

                        {/* 用户选项 */}
                        {users
                          .filter(
                            (user) =>
                              user.username
                                .toLowerCase()
                                .includes(userFilterSearch.toLowerCase()) ||
                              userFilterSearch === ""
                          )
                          .map((user) => (
                            <div
                              key={user.id}
                              onClick={() => {
                                handleAssignmentFilterChange(
                                  `${user.username}`
                                );
                                setShowUserFilterSearch(false);
                              }}
                              style={{
                                padding: "10px 15px",
                                cursor: "pointer",
                                borderBottom: "1px solid #eee",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                              onMouseEnter={(e) =>
                                (e.target.style.backgroundColor = "#f5f5f5")
                              }
                              onMouseLeave={(e) =>
                                (e.target.style.backgroundColor = "white")
                              }
                            >
                              <span>{user.username}</span>
                              {user.is_superuser && (
                                <span
                                  style={{
                                    backgroundColor: "#4f46e5",
                                    color: "white",
                                    borderRadius: "12px",
                                    padding: "2px 8px",
                                    fontSize: "12px",
                                  }}
                                >
                                  管理员
                                </span>
                              )}
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            <div className="filter-group">
              <label htmlFor="pubkeyFilter">公钥:</label>
              <div style={{ position: "relative" }}>
                <input
                  type="text"
                  id="pubkeyFilter"
                  value={pubkeyFilter}
                  onChange={handlePubkeyFilterChange}
                  placeholder="输入公钥进行搜索..."
                  className="filter-input"
                  style={{
                    width: "100%",
                    padding: "10px 30px 10px 10px",
                    opacity: batchOperationLoading ? 0.7 : 1,
                    cursor: batchOperationLoading ? "not-allowed" : "text",
                  }}
                  disabled={batchOperationLoading}
                />
                {pubkeyFilter && !batchOperationLoading && (
                  <button
                    onClick={clearPubkeyFilter}
                    style={{
                      position: "absolute",
                      right: "5px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      fontSize: "18px",
                      padding: "0",
                      width: "20px",
                      height: "20px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      opacity: batchOperationLoading ? 0.5 : 1,
                    }}
                    disabled={batchOperationLoading}
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
            <div
              className="filter-group"
              style={{ flexDirection: "row", gap: "15px" }}
            >
              <button
                onClick={resetFilters}
                className="secondary-button"
                style={{
                  height: "40px",
                  opacity: batchOperationLoading ? 0.7 : 1,
                  cursor: batchOperationLoading ? "not-allowed" : "pointer",
                }}
                disabled={batchOperationLoading}
              >
                重置过滤器
              </button>
              {isAdmin && (
                <button
                  onClick={createNode}
                  className="success-button"
                  style={{ alignSelf: "flex-end", height: "40px" }}
                  disabled={batchOperationLoading}
                >
                  新增节点
                </button>
              )}
            </div>

            {/* 批量操作下拉框 */}
            {isAdmin && selectedNodes.size > 0 && (
              <div className="filter-group" style={{ position: "relative" }}>
                <button
                  onClick={() => setShowBatchDropdown(!showBatchDropdown)}
                  className="secondary-button"
                  disabled={batchOperationLoading}
                  style={{
                    height: "40px",
                    padding: "0 15px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    minWidth: "120px",
                    opacity: batchOperationLoading ? 0.7 : 1,
                    cursor: batchOperationLoading ? "not-allowed" : "pointer",
                  }}
                >
                  {batchOperationLoading
                    ? "处理中..."
                    : `批量操作 (${selectedNodes.size})`}
                  <span style={{ marginLeft: "5px" }}>
                    {batchOperationLoading ? "⏳" : "▼"}
                  </span>
                </button>

                {showBatchDropdown && (
                  <div
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      backgroundColor: "white",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                      zIndex: 100,
                      minWidth: "120px",
                      marginTop: "5px",
                    }}
                  >
                    <div
                      style={{
                        padding: "10px",
                        borderBottom: "1px solid #eee",
                      }}
                    >
                      已选择 {selectedNodes.size} 个节点
                    </div>
                    <button
                      onClick={async () => {
                        await handleBatchStartNodes();
                        setShowBatchDropdown(false);
                      }}
                      className="primary-button"
                      disabled={batchOperationLoading}
                      style={{
                        width: "100%",
                        textAlign: "left",
                        border: "none",
                        background: "none",
                        padding: "10px 15px",
                        color: "#007bff",
                        borderRadius: "0",
                        cursor: batchOperationLoading
                          ? "not-allowed"
                          : "pointer",
                        opacity: batchOperationLoading ? 0.7 : 1,
                      }}
                    >
                      {batchOperationLoading ? "启动中..." : "批量启动"}
                    </button>
                    <button
                      onClick={async () => {
                        await handleBatchStopNodes();
                        setShowBatchDropdown(false);
                      }}
                      className="danger-button"
                      disabled={batchOperationLoading}
                      style={{
                        width: "100%",
                        textAlign: "left",
                        border: "none",
                        background: "none",
                        padding: "10px 15px",
                        color: "#dc3545",
                        borderRadius: "0",
                        cursor: batchOperationLoading
                          ? "not-allowed"
                          : "pointer",
                        opacity: batchOperationLoading ? 0.7 : 1,
                      }}
                    >
                      {batchOperationLoading ? "停止中..." : "批量停止"}
                    </button>
                    <button
                      onClick={() => {
                        setShowBatchDropdown(false);
                        openAssignModal();
                      }}
                      className="secondary-button"
                      disabled={assignLoading}
                      style={{
                        width: "100%",
                        textAlign: "left",
                        border: "none",
                        background: "none",
                        padding: "10px 15px",
                        color: "#007bff",
                        borderRadius: "0",
                        cursor: assignLoading ? "not-allowed" : "pointer",
                        opacity: assignLoading ? 0.7 : 1,
                      }}
                    >
                      {assignLoading ? "分配中..." : "分配管理员"}
                    </button>
                    <button
                      onClick={() => {
                        setShowBatchDropdown(false);
                        handleRemoveAssignNodes();
                      }}
                      className="secondary-button"
                      disabled={assignLoading}
                      style={{
                        width: "100%",
                        textAlign: "left",
                        border: "none",
                        background: "none",
                        padding: "10px 15px",
                        color: "#dc3545",
                        borderRadius: "0",
                        cursor: assignLoading ? "not-allowed" : "pointer",
                        opacity: assignLoading ? 0.7 : 1,
                      }}
                    >
                      {assignLoading ? "取消分配中..." : "取消管理员"}
                    </button>
                    <button
                      onClick={() => {
                        setSelectedNodes(new Set());
                        setShowBatchDropdown(false);
                      }}
                      className="secondary-button"
                      disabled={batchOperationLoading}
                      style={{
                        width: "100%",
                        textAlign: "left",
                        border: "none",
                        background: "none",
                        padding: "10px 15px",
                        color: "#6c757d",
                        borderRadius: "0",
                        cursor: batchOperationLoading
                          ? "not-allowed"
                          : "pointer",
                        opacity: batchOperationLoading ? 0.7 : 1,
                      }}
                    >
                      取消选择
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
          {/* 节点统计 */}
          <div
            className="nodes-summary"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "10px",
              padding: "15px 0",
            }}
          >
            <div
              style={{ display: "flex", alignItems: "center", gap: "15px" }}
            >
              <span>共找到 {totalNodes} 个节点</span>

              {isAdmin && selectedNodes.size > 0 && (
                <span style={{ fontWeight: "normal" }}>
                  (已选择 {selectedNodes.size} 个节点)
                </span>
              )}
              {isAdmin && nodes.length > 0 && (
                <div>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                      cursor: batchOperationLoading
                        ? "not-allowed"
                        : "pointer",
                      opacity: batchOperationLoading ? 0.7 : 1,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={
                        nodes.length > 0 &&
                        nodes.every((node) =>
                          selectedNodes.has(node.pubkey)
                        )
                      }
                      onChange={handleSelectAll}
                      disabled={batchOperationLoading}
                      style={{
                        cursor: batchOperationLoading
                          ? "not-allowed"
                          : "pointer",
                      }}
                    />
                    {nodes.length > 0 &&
                      nodes.every((node) => selectedNodes.has(node.pubkey))
                      ? " 取消全选"
                      : " 全选"}
                  </label>
                </div>
              )}
            </div>
            {/* 刷新图标和下拉菜单 */}
            <div className="filter-group2">
              <button
                onClick={() => {
                    manualRefreshRef.current = true;
                    refreshNodes();
                    if(isAdmin){
                      fetchUsers();
                    }
                  }
                }
                style={{
                  background: expiringFilter ? "#eaf4ff" : "none",
                  border: expiringFilter ? "1px solid #79b8ff" : "1px solid #ddd",
                  borderRadius: "4px",
                  padding: "2px 8px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                  fontSize: "small",
                }}
                disabled={loading}
                title="刷新节点数据"
              >
                <span>↻</span>
                <span>{expiringFilter ? "刷新(筛选中)" : "刷新"}</span>
              </button>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowRefreshDropdown(!showRefreshDropdown);
                }}
                style={{
                  background: "none",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  padding: "2px 8px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                  marginLeft: "5px",
                  fontSize: "small",
                }}
                title="设置自动刷新"
              >
                <span>⏱</span>
                <span>
                  {autoRefreshEnabled ? `${refreshInterval}秒` : "自动"}
                </span>
              </button>
              <label
                style={{
                  background: expiringFilter ? "#fff3f3" : "none",
                  border: expiringFilter ? "1px solid #ff6b6b" : "1px solid #ddd",
                  borderRadius: "4px",
                  padding: "2px 8px",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  marginLeft: "5px",
                  fontSize: "small",
                  cursor: "pointer",
                  marginBottom: "0",
                }}
                title="仅显示未来30天内到期节点"
              >
                <input
                  type="checkbox"
                  checked={expiringFilter}
                  onChange={(e) => handleExpiringFilterChange(e.target.checked)}
                />
                近30天到期
                {expiringFilter && (
                  <span
                    style={{
                      backgroundColor: "#ffefef",
                      color: "#c53030",
                      border: "1px solid #ffc9c9",
                      borderRadius: "10px",
                      padding: "0 6px",
                      fontSize: "11px",
                    }}
                  >
                    筛选中
                  </span>
                )}
              </label>

              {showRefreshDropdown && (
                <div
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    backgroundColor: "white",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                    zIndex: 100,
                    minWidth: "120px",
                    marginTop: "5px",
                    padding: "5px 0",
                  }}
                >
                  <div
                    style={{
                      padding: "8px 12px",
                      fontSize: "14px",
                      color: "#666",
                    }}
                  >
                    刷新间隔
                  </div>
                  {[5, 10, 30, 60, 120].map((interval) => (
                    <button
                      key={interval}
                      onClick={() => {
                        setRefreshInterval(interval);
                        // setShowRefreshDropdown(false);

                        // 如果已启用自动刷新，则重新安排
                        if (autoRefreshEnabled) {
                          scheduleNextRefresh();
                          setSuccess(`刷新间隔已更新为${interval}秒`);
                          setTimeout(() => setSuccess(""), 3000);
                        }
                      }}
                      style={{
                        width: "100%",
                        textAlign: "left",
                        border: "none",
                        background: "none",
                        padding: "8px 12px",
                        cursor: "pointer",
                      }}
                      onMouseEnter={(e) =>
                        (e.target.style.backgroundColor = "#f5f5f5")
                      }
                      onMouseLeave={(e) =>
                        (e.target.style.backgroundColor = "white")
                      }
                    >
                      {interval}秒 {refreshInterval === interval && "✓"}
                    </button>
                  ))}
                  <div
                    style={{
                      borderTop: "1px solid #eee",
                      padding: "8px 12px",
                    }}
                  >
                    <button
                      onClick={() => {
                        toggleAutoRefresh();
                        setShowRefreshDropdown(false);
                      }}
                      style={{
                        width: "100%",
                        border: "none",
                        background: "none",
                        padding: "0",
                        cursor: "pointer",
                        color: autoRefreshEnabled ? "#dc3545" : "#28a745",
                        fontWeight: "500",
                      }}
                    >
                      {autoRefreshEnabled ? "停止自动刷新" : "启动自动刷新"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="nodes-grid">
            {nodes.map((node) => (
              <div
                className={`node-card ${selectedNodes.has(node.pubkey) ? "selected" : ""
                  }`}
                key={node.id}
                style={
                  selectedNodes.has(node.pubkey)
                    ? {
                      border: "2px solid #007bff",
                      backgroundColor: "#f8f9ff",
                    }
                    : {}
                }
              >
                <div className="node-header">
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    {isAdmin && (
                      <input
                        type="checkbox"
                        checked={selectedNodes.has(node.pubkey)}
                        onChange={() => handleNodeSelect(node.pubkey)}
                        onClick={(e) => e.stopPropagation()}
                        disabled={batchOperationLoading}
                      />
                    )}
                    <h3>节点 #{node.validatorIndex}</h3>
                  </div>
                  <span
                    className={`status-badge ${getStatusClass(
                      node.runningStatus
                    )}`}
                  >
                    {node.runningStatus}
                  </span>
                </div>

                <div className="node-details">
                  <div className="node-info-item">
                    <span className="info-label">公钥:</span>
                    <span
                      className="info-value pubkey-full"
                      title={node.pubkey}
                      onClick={(e) => {
                        if (!batchOperationLoading) {
                          copyToClipboard(node.pubkey, e);
                        }
                      }}
                      style={{
                        cursor: batchOperationLoading
                          ? "not-allowed"
                          : "pointer",
                        position: "relative",
                        opacity: batchOperationLoading ? 0.7 : 1,
                      }}
                    >
                      {formatPublicKey(node.pubkey)}
                      <span
                        className="copy-icon"
                        style={{ marginLeft: "5px" }}
                      >
                        📋
                      </span>
                    </span>
                  </div>

                  <div className="node-info-item">
                    <span className="info-label">余额:</span>
                    <span className="info-value balance">
                      {parseFloat(node.balance).toFixed(4)} OR
                    </span>
                  </div>

                  <div className="node-info-item">
                    <span className="info-label">24小时奖励:</span>
                    <span
                      className={`info-value rewards ${parseFloat(node.rewards24h) > 0 ? "positive" : ""
                        }`}
                    >
                      +{parseFloat(node.rewards24h).toFixed(6)} OR
                    </span>
                  </div>

                  <div className="node-info-item">
                    <span className="info-label">质押状态:</span>
                    <span
                      className={`status-badge ${getStatusClass(
                        node.stakeStatus
                      )}`}
                    >
                      {node.stakeStatus}
                    </span>
                  </div>

                  {node.assigned_username && (
                    <div className="node-info-item">
                      <span className="info-label">分配给:</span>
                      <span className="info-value">
                        <span
                          style={{
                            backgroundColor: "#e3f2fd",
                            padding: "2px 8px",
                            borderRadius: "12px",
                            color: "#1976d2",
                            fontWeight: "500",
                          }}
                        >
                          {node.assigned_username}
                        </span>
                      </span>
                    </div>
                  )}

                  {node.assigned_admin && (
                    <div className="node-info-item">
                      <span className="info-label">分配管理员:</span>
                      <span className="info-value">
                        <span
                          style={{
                            backgroundColor: "#e3f2fd",
                            padding: "2px 8px",
                            borderRadius: "12px",
                            color: "#195f12ff",
                            fontWeight: "500",
                          }}
                        >
                          {node.assigned_admin}
                        </span>
                      </span>
                    </div>
                  )}

                  <div className="node-info-item">
                    <span className="info-label">最后更新:</span>
                    <span className="info-value">
                      {node.lastUpdateTime
                        ? new Date(
                          node.lastUpdateTime * 1000
                        ).toLocaleString()
                        : "N/A"}
                    </span>
                  </div>
                  
                  {node.rental_time && (
                    <div className="node-info-item">
                      <span className="info-label">到期时间:</span>
                      <span
                        className="info-value"
                        style={(() => {
                          const rt = node.rental_time;
                          const ms = rt < 1000000000000 ? rt * 1000 : rt;
                          const expireMs = ms + 365 * 24 * 60 * 60 * 1000;
                          const diff = expireMs - Date.now();
                          const soon = diff > 0 && diff <= 30 * 24 * 60 * 60 * 1000;
                          return { color: soon ? "#dc3545" : undefined, fontWeight: soon ? 600 : undefined };
                        })()}
                      >
                        {(() => {
                          const rt = node.rental_time;
                          const ms = rt < 1000000000000 ? rt * 1000 : rt;
                          const expireMs =
                            ms + 365 * 24 * 60 * 60 * 1000;
                          return new Date(expireMs).toLocaleString();
                        })()}
                      </span>
                    </div>
                  )}

                  {/* 操作按钮 */}
                  <div
                    className="node-info-item"
                    style={{
                      justifyContent: "flex-end",
                      paddingTop: "10px",
                      gap: "10px",
                    }}
                  >
                    <button
                      className="secondary-button"
                      style={{ padding: "6px 12px", fontSize: "14px" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!batchOperationLoading) {
                          viewPublicKeyContent(node.pubkey);
                        }
                      }}
                      disabled={batchOperationLoading}
                    >
                      查看
                    </button>
                    {isAdmin && (
                      <>
                        <button
                          className="secondary-button"
                          style={{ padding: "6px 12px", fontSize: "14px" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!batchOperationLoading) {
                              handleModify(node.pubkey);
                            }
                          }}
                          disabled={batchOperationLoading}
                        >
                          修改
                        </button>
                        {node.runningStatus.toLowerCase() === "active" ? (
                          <button
                            className="danger-button"
                            style={{
                              padding: "6px 12px",
                              fontSize: "14px",
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!batchOperationLoading) {
                                handleStopNode(node.pubkey);
                              }
                            }}
                            disabled={batchOperationLoading}
                          >
                            {batchOperationLoading
                              ? "停止中..."
                              : "停止节点"}
                          </button>
                        ) : (
                          <button
                            className="primary-button"
                            style={{
                              padding: "6px 12px",
                              fontSize: "14px",
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!batchOperationLoading) {
                                handleStartNode(node.pubkey);
                              }
                            }}
                            disabled={batchOperationLoading}
                          >
                            {batchOperationLoading
                              ? "启动中..."
                              : "启动节点"}
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          {nodes.length == 0 && (<div className="no-nodes">
              <div className="empty-state">
                <h3>暂无节点</h3>
                <p>您还没有添加任何节点。请添加新节点以开始使用。</p>
              </div>
          </div>)}
        </div>

        {/* 分页控件 */}
        {totalPages > 1 && (
          <div className="pagination">
            <button
              onClick={prevPage}
              disabled={currentPage === 1 || batchOperationLoading}
              className="pagination-button"
              style={{
                opacity: batchOperationLoading ? 0.7 : 1,
                cursor: batchOperationLoading ? "not-allowed" : "pointer",
              }}
            >
              上一页
            </button>

            {[...Array(totalPages).keys()].map((number) => {
              // 当总页数超过3页时，只显示3页：第一页、当前页、最后一页
              if (totalPages > 3) {
                // 始终显示第一页
                if (number === 0) {
                  return (
                    <button
                      key={number + 1}
                      onClick={() => {
                        if (!batchOperationLoading) {
                          paginate(number + 1);
                        }
                      }}
                      className={`pagination-button page-number ${currentPage === number + 1 ? "active" : ""
                        }`}
                      disabled={batchOperationLoading}
                      style={{
                        opacity: batchOperationLoading ? 0.7 : 1,
                        cursor: batchOperationLoading
                          ? "not-allowed"
                          : "pointer",
                      }}
                    >
                      {number + 1}
                    </button>
                  );
                }

                // 当前页左边显示省略号
                if (number === currentPage - 2 && currentPage > 2) {
                  return (
                    <span
                      key={`ellipsis-${number}`}
                      className="pagination-ellipsis"
                    >
                      ...
                    </span>
                  );
                }

                // 显示当前页
                if (number === currentPage - 1) {
                  return (
                    <button
                      key={number + 1}
                      onClick={() => {
                        if (!batchOperationLoading) {
                          paginate(number + 1);
                        }
                      }}
                      className={`pagination-button page-number ${currentPage === number + 1 ? "active" : ""
                        }`}
                      disabled={batchOperationLoading}
                      style={{
                        opacity: batchOperationLoading ? 0.7 : 1,
                        cursor: batchOperationLoading
                          ? "not-allowed"
                          : "pointer",
                      }}
                    >
                      {number + 1}
                    </button>
                  );
                }

                // 当前页右边显示省略号
                if (number === currentPage && currentPage < totalPages - 1) {
                  return (
                    <span
                      key={`ellipsis-${number}`}
                      className="pagination-ellipsis"
                    >
                      ...
                    </span>
                  );
                }

                // 始终显示最后一页
                if (number === totalPages - 1) {
                  return (
                    <button
                      key={number + 1}
                      onClick={() => {
                        if (!batchOperationLoading) {
                          paginate(number + 1);
                        }
                      }}
                      className={`pagination-button page-number ${currentPage === number + 1 ? "active" : ""
                        }`}
                      disabled={batchOperationLoading}
                      style={{
                        opacity: batchOperationLoading ? 0.7 : 1,
                        cursor: batchOperationLoading
                          ? "not-allowed"
                          : "pointer",
                      }}
                    >
                      {number + 1}
                    </button>
                  );
                }

                return null;
              } else {
                // 总页数不超过3页时，显示所有页码
                return (
                  <button
                    key={number + 1}
                    onClick={() => {
                      if (!batchOperationLoading) {
                        paginate(number + 1);
                      }
                    }}
                    className={`pagination-button page-number ${currentPage === number + 1 ? "active" : ""
                      }`}
                    disabled={batchOperationLoading}
                    style={{
                      opacity: batchOperationLoading ? 0.7 : 1,
                      cursor: batchOperationLoading ? "not-allowed" : "pointer",
                    }}
                  >
                    {number + 1}
                  </button>
                );
              }
            })}

            <button
              onClick={nextPage}
              disabled={currentPage === totalPages || batchOperationLoading}
              className="pagination-button"
              style={{
                opacity: batchOperationLoading ? 0.7 : 1,
                cursor: batchOperationLoading ? "not-allowed" : "pointer",
              }}
            >
              下一页
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default NodeManagement;
